import React, { useMemo } from "react";
// CommonJS builds on purpose: mhchem does require("katex"), so both resolve to the
// same KaTeX instance and \ce / \pu are registered on the one we render with
import katex from "katex/dist/katex.js";
import "katex/dist/contrib/mhchem.js";
import "katex/dist/katex.min.css";
import "./MathText.css";
import { BLOCK_TAGS, VOID_TAGS, parseRichHtml } from "../utils/richText";

// Delimited maths: \[ … \] and $$ … $$ display, \( … \) and $ … $ inline.
// Two-character delimiters come first so they win over single dollars.
const DELIMITED = /(\\\[[\s\S]*?\\\]|\\\([\s\S]*?\\\)|\$\$[\s\S]*?\$\$|(?<![\\$])\$(?=\S)[^$\n]*?\S\$(?!\d))/g;

const toMathPart = (part) => {
  if (part.startsWith("\\[") && part.endsWith("\\]")) return { type: "display", content: part.slice(2, -2) };
  if (part.startsWith("\\(") && part.endsWith("\\)")) return { type: "inline", content: part.slice(2, -2) };
  if (part.startsWith("$$") && part.endsWith("$$") && part.length > 4) return { type: "display", content: part.slice(2, -2) };
  if (part.startsWith("$") && part.endsWith("$") && part.length > 2) return { type: "inline", content: part.slice(1, -1) };
  return null;
};

// ---- Bare LaTeX (no delimiters) ----
// The math toolbar inserts commands like \frac{a}{b} without wrapping them, so
// saved text often has LaTeX sitting in plain prose. Pick those runs out and
// typeset them; anything KaTeX can't parse stays as the original text.

const TRIGGER = /\\[a-zA-Z]+|\\[{}|,;:!% ]|[\^_](?=\s*[{A-Za-z0-9\\])/g;
const WALK_BACK = /[A-Za-z0-9(){}[\]+\-*/=.'|]/;
const MATH_CHAR = /[0-9+\-*/=<>()[\]|.,:;'!&]/;
const LETTER = /[A-Za-z]/;
const TRAILING = /[\s.,;:!?]+$/;
// mhchem already typesets everything inside its braces, so a run is exactly the
// command — scanning on would swallow prose like "; (ii)" or "288 K" into italics
const CHEM = /^\\(?:ce|pu)\s*\{/;

// Index just past the brace group opening at or after `from`
const braceGroupEnd = (text, from) => {
  let i = text.indexOf("{", from);
  let depth = 0;
  while (i < text.length) {
    if (text[i] === "\\") i += 1;
    else if (text[i] === "{") depth += 1;
    else if (text[i] === "}" && --depth === 0) return i + 1;
    i += 1;
  }
  return text.length;
};

// End index of the maths run that contains the trigger at `from`
const scanForward = (text, from) => {
  let i = from;
  let end = from;
  while (i < text.length) {
    const ch = text[i];
    if (ch === "\\") {
      const cmd = /^\\(?:[a-zA-Z]+|.)/.exec(text.slice(i));
      i += cmd ? cmd[0].length : 1;
      end = i;
    } else if (ch === "{") {
      let depth = 0;
      while (i < text.length) {
        if (text[i] === "\\") i += 1;
        else if (text[i] === "{") depth += 1;
        else if (text[i] === "}" && --depth === 0) break;
        i += 1;
      }
      i += 1;
      end = Math.min(i, text.length);
    } else if (ch === "^" || ch === "_" || ch === "}") {
      i += 1;
      end = i;
    } else if (LETTER.test(ch)) {
      let j = i;
      while (j < text.length && LETTER.test(text[j])) j += 1;
      const word = text.slice(i, j);
      // A standalone word of two or more letters is prose ("is", "the"), except differentials like dx
      const standalone = i > 0 && /\s/.test(text[i - 1]);
      if (standalone && word.length > 1 && !/^d[a-z]$/.test(word)) break;
      i = j;
      end = i;
    } else if (MATH_CHAR.test(ch)) {
      i += 1;
      end = i;
    } else if (ch === " " || ch === "\t") {
      i += 1; // maths may continue after the space; `end` only moves on real content
    } else {
      break; // newline, Devanagari, quotes, question marks …
    }
  }
  return end;
};

const canTypeset = (latex) => {
  try {
    katex.renderToString(latex, { throwOnError: true, strict: "ignore" });
    return true;
  } catch {
    return false;
  }
};

const splitBareLatex = (text) => {
  const parts = [];
  let cursor = 0;
  TRIGGER.lastIndex = 0;
  let match;

  while ((match = TRIGGER.exec(text)) !== null) {
    if (match.index < cursor) continue;

    let start = match.index;
    let end;
    if (CHEM.test(text.slice(match.index))) {
      end = braceGroupEnd(text, match.index);
    } else {
      while (start > cursor && WALK_BACK.test(text[start - 1])) start -= 1;
      end = scanForward(text, match.index);
      const trailing = TRAILING.exec(text.slice(start, end));
      if (trailing) end -= trailing[0].length;
    }

    const latex = text.slice(start, end);
    if (end > start && canTypeset(latex)) {
      if (start > cursor) parts.push({ type: "text", content: text.slice(cursor, start) });
      parts.push({ type: "inline", content: latex });
      cursor = end;
    }
    TRIGGER.lastIndex = Math.max(end, match.index + match[0].length);
  }

  if (cursor < text.length) parts.push({ type: "text", content: text.slice(cursor) });
  return parts;
};

// Split text into plain-text and maths parts
export const splitMath = (text) => {
  if (!text) return [];
  return String(text)
    .split(DELIMITED)
    .filter(Boolean)
    .flatMap((part) => {
      const math = toMathPart(part);
      return math ? [math] : splitBareLatex(part);
    });
};

export const renderLatex = (latex, displayMode) => {
  try {
    return katex.renderToString(latex, {
      displayMode,
      throwOnError: false, // a malformed formula shows as red source instead of blanking the question
      strict: "ignore",
      trust: false,
      errorColor: "#d43c3c",
    });
  } catch {
    return null;
  }
};

const renderParts = (parts, keyPrefix = "") =>
  parts.map((part, idx) => {
    const key = `${keyPrefix}${idx}`;
    if (part.type === "text") return <React.Fragment key={key}>{part.content}</React.Fragment>;

    const display = part.type === "display";
    const html = renderLatex(part.content, display);
    if (!html) return <code key={key} className="math-text-raw">{part.content}</code>;
    return (
      <span
        key={key}
        className={display ? "math-text-display" : "math-text-inline"}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  });

// Clean up doubly-escaped LaTeX coming back from the API
const cleanLatex = (text) =>
  !text
    ? ""
    : String(text)
        .replace(/\\\\([()])/g, "\\$1")
        .replace(/\\\\\[/g, "\\[")
        .replace(/\\\\\]/g, "\\]")
        .replace(/\\\\\{/g, "\\{")
        .replace(/\\\\\}/g, "\\}")
        .replace(/\\\\\\/g, "\\\\");

// A tree from parseRichHtml, with LaTeX typeset in its text. inline: block tags become block spans,
// so the result can sit inside an option label or a sentence.
const renderRich = (nodes, keyPrefix, inline) =>
  nodes.map((node, idx) => {
    const key = `${keyPrefix}${idx}`;
    if (node.type === "text") return <React.Fragment key={key}>{renderParts(splitMath(node.value), `${key}-`)}</React.Fragment>;

    const asSpan = inline && BLOCK_TAGS.has(node.tag) && !VOID_TAGS.has(node.tag);
    const props = { key, ...node.attrs, ...(asSpan ? { className: "math-text-block" } : {}) };
    if (VOID_TAGS.has(node.tag)) return React.createElement(node.tag, props);
    return React.createElement(asSpan ? "span" : node.tag, props, renderRich(node.children, `${key}-`, inline));
  });

// Text with LaTeX; HTML formatting (from Word, Docs or a CSV) is shown as formatting, never as raw tags.
// inline: render in a <span> (e.g. inside an option label or next to a letter)
export function MathText({ text, empty = null, className = "", inline = false }) {
  const cleaned = useMemo(() => cleanLatex(text), [text]);
  const rich = useMemo(() => parseRichHtml(cleaned), [cleaned]);
  const parts = useMemo(() => (rich ? null : splitMath(cleaned)), [rich, cleaned]);
  if (!text) return empty;

  const Tag = inline ? "span" : "div";
  return (
    <Tag className={`math-text ${rich ? "math-text-html" : ""} ${className}`.trim()}>
      {rich ? renderRich(rich, "h", inline) : renderParts(parts)}
    </Tag>
  );
}

// Read-only, live-rendered view of what's typed in an editor field
export function MathPreview({ text, placeholder = "Preview appears here as you type" }) {
  return (
    <div className="math-preview" aria-live="polite">
      <span className="math-preview-label">
        <i className="bi bi-eye"></i> Preview
      </span>
      <MathText text={text} empty={<span className="math-preview-empty">{placeholder}</span>} />
    </div>
  );
}

// Render WYSIWYG HTML (paragraphs) while still typesetting LaTeX in text nodes.
// Parsed with the same safe reader as MathText (no scripts, no event handlers, no styles).
export function HtmlWithEquations({ html }) {
  if (!html) return null;
  return <MathText text={html} className="html-with-equations" />;
}
